import { useEffect, useState, type ReactNode } from "react";
import { useAuth, useConfig, type AuthState } from "./provider";
import { applyTenantTheme, type TenantTheme } from "./theme";
import type { WorkspaceConfig } from "./config";

/** What the left panel says about the customer, from GET /tenant/public (unauthenticated). */
export interface SigninCopy { display_name: string; signin_blurb: string; logo_url?: string | null; theme?: TenantTheme | null }

// Used until GET /tenant/public answers, and if it never does: the page still reads as one sentence without a name.
const FALLBACK: SigninCopy = { display_name: "", signin_blurb: "Agents draft scopes of work and check them against your rules. People make every decision.", logo_url: null, theme: null };

export function useSigninCopy(apiBase: string): SigninCopy | null {
  const [copy, setCopy] = useState<SigninCopy | null>(null);
  useEffect(() => {
    let live = true;
    if (typeof fetch !== "function") { setCopy(FALLBACK); return; }
    fetch(`${apiBase}/tenant/public`, { headers: { accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : FALLBACK))
      .then((c: SigninCopy) => { if (live) { applyTenantTheme(c.theme); setCopy(c); } })   // the customer's colour, before and after sign-in
      .catch(() => { if (live) setCopy(FALLBACK); });
    return () => { live = false; };
  }, [apiBase]);
  return copy;
}

function Arrow() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></svg>;
}

function Spinner() {
  return <span className="signin-spinner" role="presentation" aria-hidden />;
}

function Notice({ children }: { children: ReactNode }) {
  return (
    <p className="signin-notice" role="status">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M12 11v5" strokeLinecap="round" /></svg>
      <span>{children}</span>
    </p>
  );
}

/** The two-panel sign-in page (BRIEF_signin_page): the customer on the left, the one action on the right. */
/** The customer's own mark, when they have one: an absolute URL, or a path served by their API. */
export function TenantLogo({ copy, apiBase, className = "signin-tenant-logo" }: { copy: SigninCopy; apiBase: string; className?: string }) {
  if (!copy.logo_url) return null;
  const src = /^https?:\/\//.test(copy.logo_url) ? copy.logo_url : `${apiBase}${copy.logo_url}`;
  return <img className={className} src={src} alt={copy.display_name} />;
}

export function SignInPage({ copy, config, auth, failed, busy, children }: {
  copy: SigninCopy | null; config: WorkspaceConfig; auth: AuthState; failed?: boolean; busy?: boolean; children?: ReactNode;
}) {
  const c = copy ?? FALLBACK;
  const name = c.display_name;
  const title = name ? `${name} procurement workspace` : "Procurement workspace";
  return (
    <div className="signin">
      <section className="signin-brand">
        {c.logo_url
          ? <TenantLogo copy={c} apiBase={config.api_base} className="signin-brand-logo" />
          : <img src="/brand/kreta-logo.png" alt="Kreta AI" className="signin-wordmark" />}
        <div className="signin-brand-mid">
          <h1>{title}</h1>
          <p className="signin-blurb">{c.signin_blurb}</p>
        </div>
      </section>
      <div className="signin-seam" aria-hidden />
      <section className="signin-action">
        <p className="signin-host">{typeof window === "undefined" ? "" : window.location.host}</p>
        <div className="signin-column">{children ?? <SignInColumn name={name} config={config} auth={auth} failed={failed} busy={busy} />}</div>
        <footer className="signin-foot">
          <span className="signin-by">
            {c.logo_url && <img src="/brand/kreta-logo.png" alt="" className="signin-by-mark" />}
            Deployed by Kreta AI. Your data stays in your environment.
          </span>
          <span className="signin-links">
            {config.links?.privacy && <a href={config.links.privacy}>Privacy</a>}
            {config.links?.support && <a href={config.links.support}>Support</a>}
          </span>
        </footer>
      </section>
    </div>
  );
}

function SignInColumn({ name, config, auth, failed, busy }: { name: string; config: WorkspaceConfig; auth: AuthState; failed?: boolean; busy?: boolean }) {
  const [showToken, setShowToken] = useState(false);
  const stub = config.auth_mode === "stub";
  return (
    <>
      <h2>Sign in</h2>
      <p className="signin-sub">{stub ? "This workspace is running without Auth0. Paste a token to continue." : `Use your usual ${name || "work"} account. What you can see and do here comes from it.`}</p>
      {failed && <Notice>Sign-in didn&apos;t complete. Try again. If it keeps happening, your administrator can check your account.</Notice>}
      {auth.sessionExpired && !failed && <Notice>Your sign-in token has expired. {stub ? "Paste a new one to continue." : "Sign in again to continue."}</Notice>}
      {!stub && (
        <button className="signin-primary" onClick={auth.login} disabled={busy}>
          <span>{failed ? "Try again" : name ? `Continue with ${name} sign-in` : "Continue to sign in"}</span>
          {busy ? <Spinner /> : <Arrow />}
        </button>
      )}
      <p className="signin-help">Trouble signing in? Ask your {name ? `${name} administrator` : "administrator"} to check your account.</p>
      {/* the token way in exists only where the deployment has no Auth0 at all */}
      {/* the token way in: the only one under stub, and beside Auth0 where the deployment keeps it (token_signin) */}
      {auth.loginWithToken && !showToken && <p className="signin-token-link"><button type="button" className="link" onClick={() => setShowToken(true)}>Sign in with a token instead</button></p>}
      {auth.loginWithToken && showToken && <TokenForm onUse={auth.loginWithToken} />}
    </>
  );
}

function TokenForm({ onUse }: { onUse: (token: string) => void }) {
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  return (
    <form className="signin-token" onSubmit={(e) => { e.preventDefault(); if (token.trim().split(".").length !== 3) { setError("That is not a JWT (expected three dot-separated parts)."); return; } onUse(token); }}>
      <label className="signin-token-label" htmlFor="stub-token">Paste a token minted with <code>scripts/dev_token.py</code></label>
      <textarea id="stub-token" rows={4} value={token} onChange={(e) => setToken(e.target.value)} placeholder="eyJhbGciOi..." aria-label="Stub token" />
      {error && <p className="signin-error">{error}</p>}
      <button className="signin-primary" type="submit" disabled={!token.trim()}><span>Use token</span><Arrow /></button>
    </form>
  );
}

/** Signed in, but the account is not set up for any agent yet (BRIEF_signin_page, states). */
export function NoAccessPage({ copy, config, auth }: { copy: SigninCopy | null; config: WorkspaceConfig; auth: AuthState }) {
  const email = auth.user?.email ?? auth.user?.sub ?? "this account";
  const name = (copy ?? FALLBACK).display_name || "your organisation";
  return (
    <SignInPage copy={copy} config={config} auth={auth}>
      <h2>You&apos;re signed in, but not set up yet</h2>
      <p className="signin-sub">
        {email} has no workspace access. An administrator at {name} grants it by giving the account a role:
        a <strong>business user</strong> raises requests, a <strong>procurement user</strong> runs them, and a
        <strong> subject-matter expert</strong> answers questions about their field.
      </p>
      <button className="signin-secondary" onClick={auth.logout}>Sign out</button>
      <p className="signin-help">Wrong account? Sign out and try another.</p>
    </SignInPage>
  );
}

export function SignInScreen() {
  const auth = useAuth();
  const config = useConfig();
  const copy = useSigninCopy(config.api_base);
  const failed = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("error");
  return <SignInPage copy={copy} config={config} auth={auth} failed={failed || auth.signInFailed} busy={auth.isLoading} />;
}
